import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, ArrowRight } from 'lucide-react';
import { businessInfo } from '../mockData';
import { siteConfig } from '../content/siteContent';
import { business } from '../content/business';
import { catalogueMeta } from '../content/products';
import { Button } from './ui/button';

const quickLinks = [
  { to: '/', label: 'Home' },
  { to: '/residential', label: 'Residential' },
  { to: '/commercial', label: 'Commercial' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/partners', label: 'Trade Partners' },
  { to: '/service-areas', label: 'Service Areas' },
  { to: '/blog', label: 'Tips & Guides' },
  { to: '/projects', label: 'Projects' },
  { to: '/reviews', label: 'Reviews' },
  { to: '/about', label: 'About Us' },
  { to: '/contact', label: 'Contact' },
];

const serviceLinks = [
  { to: '/residential/lock-repair-replacement', label: 'Lock Repair & Replacement' },
  { to: '/residential/deadbolt-installation', label: 'Deadbolt Installation' },
  { to: '/residential/sliding-door-locks', label: 'Ranchslider & Sliding Door Locks' },
  { to: '/residential/smart-lock-installation', label: 'Smart & Digital Locks' },
  { to: '/residential/door-handle-repair', label: 'Door Handle Repair' },
  { to: '/residential/broken-key-extraction', label: 'Broken Key Extraction' },
  { to: '/residential/home-security-upgrades', label: 'Home Security Upgrades' },
  { to: '/commercial/door-hardware-installation', label: 'Commercial Door Hardware' },
  { to: '/commercial/door-closers', label: 'Door Closers' },
  { to: '/commercial/access-control-installation', label: 'Access Control Installation' },
  { to: '/commercial/maintenance-iqp', label: 'Maintenance & IQP Support' },
];

const shopLinks = [
  { to: '/shop/find-my-lock', label: 'Find my lock' },
  { to: '/shop/e-lok', label: 'E-LOK digital locks' },
  { to: '/shop/aqara', label: 'Aqara smart locks' },
  { to: '/shop/door-automation', label: 'Automatic door openers' },
  { to: '/shop/how-to-measure', label: 'How to measure the backset' },
  { to: '/shop/will-it-fit', label: 'Will it fit? Door guide' },
  { to: '/shop/guides/fitting-a-digital-mortice-lock', label: 'How we fit a digital lock' },
  { to: '/shop/installation', label: 'Supply & install' },
  { to: '/shop/shipping-returns', label: 'Shipping & returns' },
  { to: '/shop/warranty', label: 'Warranty' },
  { to: '/shop/terms', label: 'Shop terms' },
];

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const shopLive = catalogueMeta.status === 'live';
  // In the shop, "a quote" means the walk-through → fitted price, not the project form.
  const inShop = useLocation().pathname.startsWith('/shop');

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* CTA Section */}
      <div className="bg-red-600 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
            Locks sorted — home, business or site
          </h2>
          <p className="text-lg sm:text-xl text-red-100 mb-8 max-w-3xl mx-auto">
            Mobile locksmiths and door hardware specialists. Fixed quotes before any extra work starts.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${businessInfo.phoneTel}`}>
              <Button size="lg" className="bg-white text-red-700 hover:bg-red-50 font-bold">
                <Phone className="mr-2" size={20} />
                Call {businessInfo.phone}
              </Button>
            </a>
            <Link to={inShop ? '/shop/find-my-lock' : '/contact'}>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-semibold">
                {inShop ? 'Find my lock' : 'Request a quote'}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className={`grid grid-cols-1 md:grid-cols-2 ${shopLive ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-8 lg:gap-10`}>
          {/* Company Info with Logo */}
          <div className="lg:col-span-1">
            <div style={{ flexShrink: 0, overflow: 'visible', marginBottom: '24px', paddingTop: '4px', paddingBottom: '4px' }} data-testid="footer-logo-container">
              <Link to="/">
                <img
                  src="/logo.png"
                  alt="Shore Doors Locksmiths"
                  data-testid="footer-logo-img"
                  style={{ height: '56px', width: 'auto', display: 'block' }}
                />
              </Link>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed text-sm">
              {siteConfig.brandName} — mobile locksmiths and commercial door hardware installers.
              The installation partner Auckland's security companies trust, now serving homes and
              businesses directly.
            </p>
            <div className="flex items-start gap-3 text-gray-400 mb-3">
              <MapPin size={18} className="mt-1 flex-shrink-0" />
              <span>{businessInfo.address}</span>
            </div>
            {business.googleProfileUrl && (
              <a
                href={business.googleProfileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors text-sm"
              >
                Find us on Google <ArrowRight size={14} />
              </a>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Quick Links</h3>
            <ul className="space-y-3">
              {[...quickLinks.slice(0, 4), ...(shopLive ? [{ to: '/shop', label: 'Shop' }] : []), ...quickLinks.slice(4)].map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-gray-400 hover:text-white transition-colors inline-block">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Our Services</h3>
            <ul className="space-y-3">
              {serviceLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-gray-400 hover:text-white transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Shop — listed once the catalogue is live */}
          {shopLive && (<div>
            <h3 className="text-white font-semibold text-lg mb-4">Shop</h3>
            <ul className="space-y-3">
              {shopLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-gray-400 hover:text-white transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>)}

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Contact Us</h3>
            <div className="space-y-4">
              <a href={`tel:${businessInfo.phoneTel}`} className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors">
                <Phone size={18} />
                <span>{businessInfo.phone}</span>
              </a>
              <a href={`tel:${businessInfo.mobileTel}`} className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors">
                <Phone size={18} />
                <span>{businessInfo.mobile} <span className="text-gray-500">(mobile)</span></span>
              </a>
              <a href={`mailto:${businessInfo.email}`} className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors">
                <Mail size={18} />
                <span className="break-all">{businessInfo.email}</span>
              </a>
              <div className="flex items-start gap-3 text-gray-400">
                <Clock size={18} className="mt-1 flex-shrink-0" />
                <div>
                  <p>{siteConfig.hoursLine}</p>
                  <p className="text-sm">Other work: {siteConfig.businessHoursLine}</p>
                  {business.workshop.open && (
                    <p className="text-sm">Workshop: {business.workshop.hoursLine}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400 text-sm">
          <p>&copy; {currentYear} Shore Doors. All rights reserved.</p>
          <p className="mt-2">{siteConfig.brandName} — Mobile Locksmiths & Commercial Door Hardware, Auckland</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
